import React, {useMemo} from 'react';
import {Col, Row} from 'react-bootstrap';

import {
    ConservationAction as ConservationActionModel,
    ConservationActionSampleEvent as ConservationActionSampleEventModel,
} from '../../../Models';
    
import {GenericModelViewer} from "../../Common/GenericModelViewer";
import {SurveySectionHeader} from "../../Common/SurveyForm/SurveySectionHeader";

export const ModeledUpliftSurveySection = (props) => {    

    const {
        EditMode,
        ConservationAction = {},
        Survey: survey,
        Section,
        id,
        Header,
        Footer,
        
        // handlers
        OnValidation = ((validationObject) => {}),
        OnConservationActionChange = (() => {}),
        OnSave = (() => {}),
    } = props;
    
    const {
        SurveyType: surveyType,
    } = survey ?? {};
    
    const onValidationUpdate = (validations, editorGuid) => {
        // TODO: handle this somehow?
        
        // This call gets an array of Validations, and an editorGuid.
        // The parent OnValidation takes and Object of Validations (validations: {'key': 'message'})
        // so we can't just forward the validations errors on.
        // In general, it seems like we shouldn't need to handle this?
    }
    
    const conservationActionPhaseId = surveyType?.conservationActionPhaseId;
    
    // we build an altered model to present in our GenericModelViewer on this page
    // so that we can show only the relevant subset of fields.
    const model = useMemo(() => {
            
        let conservationActionModel = new ConservationActionModel();
            
        // we modify this model's fieldDefs to present a unique view
        // of model editor on this page
        let alteredConservationActionSampleEventModel = new ConservationActionSampleEventModel();
            
        // assign a default conservationActionPhaseId value
        alteredConservationActionSampleEventModel.defaults = {
            conservationActionPhaseId: conservationActionPhaseId
        };
            
        alteredConservationActionSampleEventModel.classNamesFromData = (data) => {
            return "sampleEvent-environmentalBenefitId-" + (data?.SampleEvent?.environmentalBenefitId);
        };
            
        // and prevent the conservationActionPhaseId value from being editable
        alteredConservationActionSampleEventModel.getFlattenedFieldDefs()
            .find( (field) => field.key === "conservationActionPhaseId" )
            .editor = "ConservationActionPhaseFieldViewer";
        
        // we also modify this (association) fieldDef
        let conservationActionSampleEventsFieldDef = conservationActionModel.getFlattenedFieldDefs()
            .find( (field) => field.key === "ConservationActionSampleEvents" );
            
        // so it uses our altered ConservationActionSampleEventModel
        conservationActionSampleEventsFieldDef
            .model = alteredConservationActionSampleEventModel;
        
        // and so it doesn't display it's groupTitle since
        // we already display a title for the survey section.
        // Only the fieldDef is touched: fieldDef.association is the SHARED
        // association config (module-level _associations), and mutating it
        // here would leak into every other ConservationAction model instance
        // (it used to hide/show the CA summary's Modeled Uplift group
        // depending on which page rendered first).
        conservationActionSampleEventsFieldDef
            .groupTitle = '';

        return conservationActionModel;
    },
    [conservationActionPhaseId]
    );

    const filterConservationActionSampleEventsBySurveyType = (conservationAction, surveyType) => {

        if(!conservationAction || !surveyType) {
            return [];
        }

        const conservationActionPhaseId = surveyType?.conservationActionPhaseId;

        // filter our modeled uplift to just the appropriate
        // conservationActionPhaseId for the given surveyTypeId
        return conservationAction.ConservationActionSampleEvents
            .filter((casEvent) =>
                casEvent.conservationActionPhaseId === conservationActionPhaseId &&
                casEvent?.isCurrentModelingEvent !== false
                // && casEvent.SampleEvent.sampleEventTypeId === 2 // ignore monitoring events (this isn't need because of above)
            );
    }

    let editable = EditMode;

    // the SampleEvent conditions/benefit visibility queries read this flag from
    // __CustomData — CustomData is the only viewer context that survives the
    // list-association render path (RootData is deliberately not forwarded there).
    // Memoized so the reference only changes with the flag, keeping the list
    // nodes' memoization effective.
    const customData = useMemo(() => ({
        usesSimplifiedEnvironmentalBenefits: ConservationAction?.usesSimplifiedEnvironmentalBenefits,
    }), [ConservationAction?.usesSimplifiedEnvironmentalBenefits]);

    // all ConservationAction field groups are hidden except ConservationActionSampleEvents (Modeled Uplift)
    let hiddenGroups = model.getFieldDefGroups().filter((group) => group.key !== "Modeled Uplift").map((x) => x.key);

    // all ConservationActionSampleEvents are hidden except the
    // Modeling Events that are relevant to the current survey (surveyTypeId)
    let conservationAction = {...ConservationAction};
    conservationAction.ConservationActionSampleEvents = filterConservationActionSampleEventsBySurveyType(conservationAction, surveyType);

    return <Col key={Section.surveySectionId} id={id}>
        { Header ?? <SurveySectionHeader section={Section} /> }
        <Row>
            <Col xs={12}>
                <GenericModelViewer
                    className={"sb-survey-form-table"}
                        CustomData={customData}
                        Data={conservationAction}
                        EditMode={editable}
                        HideGroups={hiddenGroups}
                        IsNewModel={false}
                        Model={model}
                        OnValidationUpdate={onValidationUpdate}
                        RootData={conservationAction}
                        SkipBorder={false}
                        Update={OnConservationActionChange}
                />
            </Col>
        </Row>
        {Footer ?? null}
    </Col>;
}
